import { useState } from "react"
import { ArrowLeft, ExternalLink, FileDown } from "lucide-react"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { toast } from "sonner"
import { usePortalAuth, usePortalInvoice } from "@/hooks/usePortalAuth"
import { getPortalInvoiceDisplayState, getPortalInvoicePaymentState } from "@/lib/invoice-payment"
import { portalApiBlob } from "@/lib/portal-api"
import { invoiceStatusChipClass } from "@/lib/chip-styles"
import { track } from "@/lib/posthog"
import { usePageTitle } from "@/hooks/usePageTitle"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"

function formatDate(value: string) {
  return new Date(`${value}T00:00:00`).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  })
}

function formatCurrency(amount: number, currency: string) {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      maximumFractionDigits: 2,
    }).format(amount)
  } catch {
    return `${currency} ${amount.toFixed(2)}`
  }
}

export function PortalInvoiceDetail() {
  const location = useLocation()
  const navigate = useNavigate()
  const { id = "" } = useParams()
  const { data: me, isLoading: meLoading } = usePortalAuth()
  const { data: invoice, isLoading: invoiceLoading, error } = usePortalInvoice(id)
  const [downloading, setDownloading] = useState(false)

  usePageTitle(invoice ? `${invoice.invoice_number} · Client Portal` : "Invoice · Client Portal")

  async function handleDownload() {
    if (!invoice) return
    setDownloading(true)
    try {
      const blob = await portalApiBlob(`/api/portal/invoices/${invoice.id}/pdf`)
      const url = URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      link.download = `Invoice_${invoice.invoice_number}.pdf`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
      track("portal_invoice_downloaded", { invoice_id: invoice.id, source: "invoice_detail" })
    } catch {
      toast.error("Could not download this invoice")
    } finally {
      setDownloading(false)
    }
  }

  function handleBackToInvoices() {
    const navigationState = location.state as { fromPortalDashboard?: boolean } | null
    if (navigationState?.fromPortalDashboard) {
      navigate(-1)
      return
    }

    navigate("/dashboard", { replace: true })
  }

  if (meLoading || invoiceLoading) {
    return (
      <div className="bg-background flex min-h-screen items-center justify-center">
        <p className="text-muted-foreground">Loading invoice...</p>
      </div>
    )
  }

  if (error || !invoice) {
    return (
      <div className="bg-background flex min-h-screen items-center justify-center px-4">
        <Card className="w-full max-w-md">
          <CardContent className="space-y-4 py-8 text-center">
            <p className="font-medium">Invoice unavailable</p>
            <p className="text-muted-foreground text-sm">
              This invoice may have been removed or may not be available to this portal account.
            </p>
            <Button variant="outline" onClick={handleBackToInvoices}>
              Back to invoices
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  const payment = getPortalInvoicePaymentState(invoice)
  const display = getPortalInvoiceDisplayState(invoice)

  return (
    <div className="bg-background min-h-screen overflow-x-clip [overflow-wrap:anywhere]">
      <div className="mx-auto w-full max-w-5xl space-y-6 px-4 py-8 sm:py-10">
        <Button variant="ghost" size="sm" onClick={handleBackToInvoices}>
          <ArrowLeft className="h-4 w-4" />
          All invoices
        </Button>

        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0 space-y-2">
            <p className="text-muted-foreground text-sm">
              {me?.billing_entity_name || "Billing"} · {me?.client.name}
            </p>
            <div className="flex min-w-0 flex-wrap items-center gap-3">
              <h1 className="max-w-full min-w-0 text-3xl font-semibold [overflow-wrap:anywhere]">
                {invoice.invoice_number}
              </h1>
              <Badge variant="outline" className={invoiceStatusChipClass(invoice.status)}>
                {display.statusLabel}
              </Badge>
            </div>
            {invoice.description ? (
              <p className="text-muted-foreground max-w-2xl [overflow-wrap:anywhere]">
                {invoice.description}
              </p>
            ) : null}
          </div>
          <div className="flex flex-wrap gap-2">
            {payment.canPay ? (
              <Button asChild>
                <a
                  href={payment.paymentUrl!}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() =>
                    track("portal_payment_clicked", {
                      invoice_id: invoice.id,
                      destination_host: new URL(payment.paymentUrl!).hostname,
                    })
                  }
                >
                  {payment.paymentLabel}
                  <ExternalLink className="h-4 w-4" />
                </a>
              </Button>
            ) : null}
            <Button variant="outline" onClick={() => void handleDownload()} disabled={downloading}>
              <FileDown className="h-4 w-4" />
              {downloading ? "Generating..." : "Download PDF"}
            </Button>
          </div>
        </div>

        <Card className="min-w-0 overflow-hidden">
          <CardContent className="grid min-w-0 grid-cols-1 gap-6 py-6 sm:grid-cols-3">
            <div>
              <p className="text-muted-foreground text-xs tracking-wide uppercase">
                {display.amountLabel}
              </p>
              <p className="mt-1 text-2xl font-semibold">
                {formatCurrency(
                  display.isLegacyCredit ? invoice.total : payment.amountDue,
                  invoice.currency
                )}
              </p>
              {display.isLegacyCredit ? (
                <p className="text-muted-foreground mt-1 text-sm">
                  Read-only historical credit. No payment is due.
                </p>
              ) : invoice.amount_paid > 0 ? (
                <p className="text-muted-foreground mt-1 text-sm">
                  {formatCurrency(invoice.amount_paid, invoice.currency)} recorded as paid
                </p>
              ) : null}
            </div>
            <div>
              <p className="text-muted-foreground text-xs tracking-wide uppercase">
                {display.isLegacyCredit ? "Record date" : "Due date"}
              </p>
              <p className="mt-1 text-lg font-semibold">
                {formatDate(display.isLegacyCredit ? invoice.issue_date : invoice.due_date)}
              </p>
              <p className="text-muted-foreground mt-1 text-sm">
                {display.isLegacyCredit
                  ? "Preserved from historical billing"
                  : `Issued ${formatDate(invoice.issue_date)}`}
              </p>
            </div>
            <div>
              <p className="text-muted-foreground text-xs tracking-wide uppercase">
                {display.totalLabel}
              </p>
              <p className="mt-1 text-lg font-semibold">
                {formatCurrency(invoice.total, invoice.currency)}
              </p>
              {invoice.purchase_order ? (
                <p className="text-muted-foreground mt-1 text-sm">PO {invoice.purchase_order}</p>
              ) : null}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{display.isLegacyCredit ? "Credit details" : "Invoice details"}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="hidden overflow-x-auto sm:block">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-muted-foreground border-b text-left">
                    <th className="py-2">Description</th>
                    <th className="py-2 text-right">Quantity</th>
                    <th className="py-2 text-right">Rate</th>
                    <th className="py-2 text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {(invoice.display_line_items || invoice.line_items).map((item) => (
                    <tr key={item.id} className="border-b last:border-0">
                      <td className="py-3">{item.description}</td>
                      <td className="py-3 text-right">{item.quantity ?? "-"}</td>
                      <td className="py-3 text-right">
                        {item.unit_cost === null
                          ? "-"
                          : formatCurrency(item.unit_cost, invoice.currency)}
                      </td>
                      <td className="py-3 text-right font-medium">
                        {formatCurrency(item.amount, invoice.currency)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="divide-y sm:hidden">
              {(invoice.display_line_items || invoice.line_items).map((item) => (
                <div key={item.id} className="space-y-2 py-3 first:pt-0 last:pb-0">
                  <p className="font-medium [overflow-wrap:anywhere]">{item.description}</p>
                  <div className="text-muted-foreground flex justify-between text-sm">
                    <span>
                      {item.quantity ?? "-"} ×{" "}
                      {item.unit_cost === null
                        ? "-"
                        : formatCurrency(item.unit_cost, invoice.currency)}
                    </span>
                    <span className="text-foreground font-medium">
                      {formatCurrency(item.amount, invoice.currency)}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-5 ml-auto max-w-sm space-y-2 border-t pt-4 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Subtotal</span>
                <span>{formatCurrency(invoice.subtotal, invoice.currency)}</span>
              </div>
              {invoice.discount > 0 ? (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Discount</span>
                  <span>−{formatCurrency(invoice.discount, invoice.currency)}</span>
                </div>
              ) : null}
              {invoice.tax_amount > 0 ? (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Tax</span>
                  <span>{formatCurrency(invoice.tax_amount, invoice.currency)}</span>
                </div>
              ) : null}
              {invoice.shipping > 0 ? (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Shipping</span>
                  <span>{formatCurrency(invoice.shipping, invoice.currency)}</span>
                </div>
              ) : null}
              <div className="flex justify-between border-t pt-2 text-base font-semibold">
                <span>Total</span>
                <span>{formatCurrency(invoice.total, invoice.currency)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid min-w-0 grid-cols-1 gap-6 md:grid-cols-2">
          {!display.isLegacyCredit && invoice.terms_snapshot ? (
            <Card className="min-w-0 overflow-hidden">
              <CardHeader>
                <CardTitle>Payment terms</CardTitle>
              </CardHeader>
              <CardContent className="text-muted-foreground space-y-2 text-sm">
                <p className="text-foreground">{invoice.terms_snapshot.payment_terms}</p>
                {invoice.terms_snapshot.late_fee_policy ? (
                  <p>{invoice.terms_snapshot.late_fee_policy}</p>
                ) : null}
                {invoice.terms_snapshot.notes ? <p>{invoice.terms_snapshot.notes}</p> : null}
              </CardContent>
            </Card>
          ) : null}

          {!display.isLegacyCredit && invoice.bank_details_snapshot ? (
            <Card className="min-w-0 overflow-hidden">
              <CardHeader>
                <CardTitle>Payment instructions</CardTitle>
              </CardHeader>
              <CardContent className="text-muted-foreground space-y-1 text-sm">
                <p className="text-foreground font-medium">
                  {invoice.bank_details_snapshot.account_name}
                </p>
                {invoice.bank_details_snapshot.bank_name ? (
                  <p>{invoice.bank_details_snapshot.bank_name}</p>
                ) : null}
                {invoice.bank_details_snapshot.account_number ? (
                  <p>
                    Account number:{" "}
                    <span className="text-foreground">
                      {invoice.bank_details_snapshot.account_number}
                    </span>
                  </p>
                ) : null}
                {invoice.bank_details_snapshot.account_type ? (
                  <p>
                    Account type:{" "}
                    <span className="text-foreground">
                      {invoice.bank_details_snapshot.account_type}
                    </span>
                  </p>
                ) : null}
                {invoice.bank_details_snapshot.routing_number ? (
                  <p>
                    Routing number:{" "}
                    <span className="text-foreground">
                      {invoice.bank_details_snapshot.routing_number}
                    </span>
                  </p>
                ) : null}
                {invoice.bank_details_snapshot.swift_bic ? (
                  <p>
                    SWIFT/BIC:{" "}
                    <span className="text-foreground">
                      {invoice.bank_details_snapshot.swift_bic}
                    </span>
                  </p>
                ) : null}
                {invoice.bank_details_snapshot.iban ? (
                  <p>
                    IBAN:{" "}
                    <span className="text-foreground">{invoice.bank_details_snapshot.iban}</span>
                  </p>
                ) : null}
                {invoice.bank_details_snapshot.instructions ? (
                  <p className="pt-2 whitespace-pre-line">
                    {invoice.bank_details_snapshot.instructions}
                  </p>
                ) : null}
              </CardContent>
            </Card>
          ) : null}
        </div>
      </div>
    </div>
  )
}
